import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import type { PfmeaItem, ControlPlanItem, ProcessStep } from '../types';

// ============================================================
// REPORT TEMPLATES
// ============================================================

export interface ReportTemplate {
    id: string;
    name: string;
    description: string;
    category: 'pfmea' | 'control' | 'management' | 'ai';
    formats: ('pdf' | 'excel' | 'csv')[];
    requiresPrime?: boolean;
    icon: string;
}

export const REPORT_TEMPLATES: ReportTemplate[] = [
    // PFMEA Analysis Reports
    {
        id: 'pfmea-worksheet',
        name: 'AIAG-VDA PFMEA Worksheet',
        description: 'Official PFMEA format per AIAG-VDA handbook',
        category: 'pfmea',
        formats: ['pdf', 'excel'],
        icon: '📋',
    },
    {
        id: 'risk-matrix',
        name: 'Risk Matrix Report',
        description: 'SxO and SxD heatmaps with item counts',
        category: 'pfmea',
        formats: ['pdf'],
        icon: '🎯',
    },
    {
        id: 'action-priority',
        name: 'Action Priority Summary',
        description: 'H/M/L breakdown with drill-down',
        category: 'pfmea',
        formats: ['pdf', 'excel'],
        icon: '⚡',
    },
    {
        id: 'top-risks',
        name: 'Top 10 Risks Report',
        description: 'Highest severity items with details',
        category: 'pfmea',
        formats: ['pdf'],
        icon: '🔥',
    },
    {
        id: '4m-analysis',
        name: '4M Cause Analysis',
        description: 'Man/Machine/Material/Method breakdown',
        category: 'pfmea',
        formats: ['pdf'],
        icon: '🔍',
    },
    {
        id: 'failure-pareto',
        name: 'Failure Mode Pareto',
        description: 'Top failure modes by frequency',
        category: 'pfmea',
        formats: ['pdf'],
        icon: '📊',
    },
    // Control & Compliance Reports
    {
        id: 'control-plan',
        name: 'Control Plan',
        description: 'Manufacturing controls from PFMEA',
        category: 'control',
        formats: ['pdf', 'excel'],
        icon: '📝',
    },
    {
        id: 'special-characteristics',
        name: 'Special Characteristics',
        description: 'CC/SC items list with controls',
        category: 'control',
        formats: ['pdf', 'excel'],
        icon: '⭐',
    },
    // Management & Audit Reports
    {
        id: 'executive-summary',
        name: 'Executive Summary',
        description: '1-page risk overview for leadership',
        category: 'management',
        formats: ['pdf'],
        icon: '📈',
    },
    {
        id: 'audit-trail',
        name: 'Audit Trail Report',
        description: 'All changes with timestamps/users',
        category: 'management',
        formats: ['pdf', 'csv'],
        icon: '🔒',
    },
    {
        id: 'action-tracker',
        name: 'Action Tracker',
        description: 'Open/closed actions with status',
        category: 'management',
        formats: ['pdf', 'excel'],
        icon: '✅',
    },
    {
        id: 'project-status',
        name: 'Project Status Report',
        description: '7-step workflow completion',
        category: 'management',
        formats: ['pdf'],
        icon: '📅',
    },
    // AI-Powered Reports (Prime Mode Only)
    {
        id: 'ai-risk-assessment',
        name: 'AI Risk Assessment',
        description: 'AI analysis of risk patterns',
        category: 'ai',
        formats: ['pdf'],
        requiresPrime: true,
        icon: '🤖',
    },
    {
        id: 'ai-recommendations',
        name: 'AI Recommendations Report',
        description: 'AI-suggested mitigations',
        category: 'ai',
        formats: ['pdf'],
        requiresPrime: true,
        icon: '💡',
    },
    {
        id: 'ai-comprehensive',
        name: 'Comprehensive Project Report',
        description: 'Full AI-narrated analysis',
        category: 'ai',
        formats: ['pdf'],
        requiresPrime: true,
        icon: '📖',
    },
];

// ============================================================
// PDF EXPORT FUNCTIONS
// ============================================================

export async function exportPfmeaWorksheetPDF(
    items: PfmeaItem[],
    projectName: string,
    standardName: string
): Promise<void> {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });

    // Header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('PFMEA Worksheet', 14, 15);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Project: ${projectName}`, 14, 22);
    doc.text(`Standard: ${standardName}`, 14, 27);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 32);

    // Table data
    const tableData = items.map(item => [
        item.processStepNumber || '',
        item.processStepName || '',
        item.processElement || '',
        item.failureMode || '',
        item.failureEffectEnd || '',
        item.failureCause || '',
        item.severity?.toString() || '',
        item.occurrence?.toString() || '',
        item.detection?.toString() || '',
        item.actionPriority || '',
        (item.preventionControls || []).join(', '),
        (item.detectionControls || []).join(', '),
        item.status || '',
    ]);

    autoTable(doc, {
        startY: 38,
        head: [[
            'Step #', 'Process Step', '4M', 'Failure Mode', 'Effect', 'Cause',
            'S', 'O', 'D', 'AP', 'Prevention', 'Detection', 'Status'
        ]],
        body: tableData,
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
            0: { cellWidth: 15 },
            1: { cellWidth: 30 },
            2: { cellWidth: 15 },
            3: { cellWidth: 35 },
            4: { cellWidth: 35 },
            5: { cellWidth: 35 },
            6: { cellWidth: 10 },
            7: { cellWidth: 10 },
            8: { cellWidth: 10 },
            9: { cellWidth: 12 },
            10: { cellWidth: 40 },
            11: { cellWidth: 40 },
            12: { cellWidth: 18 },
        },
    });

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 25, doc.internal.pageSize.height - 10);
        doc.text('Magnus PFMEA - Living Reliability Systems', 14, doc.internal.pageSize.height - 10);
    }

    doc.save(`PFMEA_Worksheet_${projectName.replace(/\s+/g, '_')}.pdf`);
}

export async function exportRiskMatrixPDF(
    items: PfmeaItem[],
    projectName: string
): Promise<void> {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    // Header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Risk Matrix Report', 14, 20);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Project: ${projectName}`, 14, 28);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 34);

    // Build SxO matrix
    const sxoMatrix: number[][] = Array(10).fill(null).map(() => Array(10).fill(0));
    items.forEach(item => {
        if (item.severity && item.occurrence) {
            sxoMatrix[item.severity - 1][item.occurrence - 1]++;
        }
    });

    // Draw SxO Matrix
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Severity × Occurrence Matrix', 14, 48);

    const cellSize = 15;
    const startX = 30;
    const startY = 55;

    // Column headers (Occurrence)
    doc.setFontSize(8);
    doc.text('Occurrence →', startX - 15, startY - 3);
    for (let o = 1; o <= 10; o++) {
        doc.text(o.toString(), startX + (o - 1) * cellSize + 5, startY - 3);
    }

    // Row headers (Severity) and cells
    doc.text('Severity ↓', startX - 25, startY + 50);
    for (let s = 10; s >= 1; s--) {
        const y = startY + (10 - s) * cellSize;
        doc.text(s.toString(), startX - 8, y + 10);

        for (let o = 1; o <= 10; o++) {
            const x = startX + (o - 1) * cellSize;
            const count = sxoMatrix[s - 1][o - 1];

            // Color based on risk level
            const riskScore = s * o;
            if (riskScore >= 80) {
                doc.setFillColor(239, 68, 68); // Red
            } else if (riskScore >= 40) {
                doc.setFillColor(251, 191, 36); // Yellow
            } else {
                doc.setFillColor(34, 197, 94); // Green
            }

            doc.rect(x, y, cellSize, cellSize, 'F');
            doc.setDrawColor(200, 200, 200);
            doc.rect(x, y, cellSize, cellSize, 'S');

            if (count > 0) {
                doc.setTextColor(255, 255, 255);
                doc.setFont('helvetica', 'bold');
                doc.text(count.toString(), x + 6, y + 10);
            }
        }
    }

    doc.setTextColor(0, 0, 0);

    // Action Priority Summary
    const apCounts = {
        H: items.filter(i => i.actionPriority === 'H').length,
        M: items.filter(i => i.actionPriority === 'M').length,
        L: items.filter(i => i.actionPriority === 'L').length,
    };

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Action Priority Summary', 14, startY + 170);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setFillColor(239, 68, 68);
    doc.rect(14, startY + 175, 20, 10, 'F');
    doc.text(`High (H): ${apCounts.H}`, 40, startY + 182);

    doc.setFillColor(251, 191, 36);
    doc.rect(14, startY + 188, 20, 10, 'F');
    doc.text(`Medium (M): ${apCounts.M}`, 40, startY + 195);

    doc.setFillColor(34, 197, 94);
    doc.rect(14, startY + 201, 20, 10, 'F');
    doc.text(`Low (L): ${apCounts.L}`, 40, startY + 208);

    doc.save(`Risk_Matrix_${projectName.replace(/\s+/g, '_')}.pdf`);
}

export async function exportExecutiveSummaryPDF(
    items: PfmeaItem[],
    projectName: string,
    workflowProgress: number
): Promise<void> {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    // Header with gradient effect
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Executive Summary', 14, 25);

    doc.setFontSize(12);
    doc.text(`Project: ${projectName}`, 14, 35);

    doc.setTextColor(0, 0, 0);

    // Key Metrics
    const apCounts = {
        H: items.filter(i => i.actionPriority === 'H').length,
        M: items.filter(i => i.actionPriority === 'M').length,
        L: items.filter(i => i.actionPriority === 'L').length,
    };

    const avgSeverity = items.length > 0
        ? (items.reduce((sum, i) => sum + (i.severity || 0), 0) / items.length).toFixed(1)
        : '0';

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Key Metrics', 14, 55);

    // Metric cards
    const metrics = [
        { label: 'Total Items', value: items.length.toString(), color: [59, 130, 246] },
        { label: 'High Priority', value: apCounts.H.toString(), color: [239, 68, 68] },
        { label: 'Avg Severity', value: avgSeverity, color: [251, 191, 36] },
        { label: 'Workflow', value: `${workflowProgress}%`, color: [34, 197, 94] },
    ];

    metrics.forEach((metric, idx) => {
        const x = 14 + idx * 47;
        doc.setFillColor(metric.color[0], metric.color[1], metric.color[2]);
        doc.roundedRect(x, 60, 44, 30, 3, 3, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text(metric.value, x + 22, 77, { align: 'center' });

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(metric.label, x + 22, 85, { align: 'center' });
    });

    doc.setTextColor(0, 0, 0);

    // Top 5 High Risk Items
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Top High-Risk Items', 14, 105);

    const highRiskItems = items
        .filter(i => i.actionPriority === 'H')
        .sort((a, b) => (b.severity || 0) - (a.severity || 0))
        .slice(0, 5);

    if (highRiskItems.length > 0) {
        autoTable(doc, {
            startY: 110,
            head: [['Process Step', 'Failure Mode', 'S', 'O', 'D', 'AP']],
            body: highRiskItems.map(item => [
                item.processStepName || '',
                item.failureMode || '',
                item.severity?.toString() || '',
                item.occurrence?.toString() || '',
                item.detection?.toString() || '',
                item.actionPriority || '',
            ]),
            styles: { fontSize: 9 },
            headStyles: { fillColor: [239, 68, 68] },
        });
    } else {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('No high-risk items found! 🎉', 14, 115);
    }

    // Footer
    doc.setFontSize(8);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 280);
    doc.text('Magnus PFMEA - Living Reliability Systems', 14, 285);

    doc.save(`Executive_Summary_${projectName.replace(/\s+/g, '_')}.pdf`);
}

// ============================================================
// EXCEL EXPORT FUNCTIONS
// ============================================================

export async function exportPfmeaWorksheetExcel(
    items: PfmeaItem[],
    projectName: string,
    standardName: string
): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Magnus PFMEA';
    workbook.created = new Date();

    const sheet = workbook.addWorksheet('PFMEA Worksheet');

    // Header info
    sheet.mergeCells('A1:M1');
    sheet.getCell('A1').value = `PFMEA Worksheet - ${projectName}`;
    sheet.getCell('A1').font = { size: 16, bold: true };

    sheet.getCell('A2').value = `Standard: ${standardName}`;
    sheet.getCell('A3').value = `Generated: ${new Date().toLocaleString()}`;

    // Column headers
    const headers = [
        'Step #', 'Process Step', '4M Element', 'Failure Mode', 'Failure Effect',
        'Failure Cause', 'S', 'O', 'D', 'AP', 'Prevention Controls',
        'Detection Controls', 'Status'
    ];

    const headerRow = sheet.addRow(headers);
    headerRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '3B82F6' }
        };
        cell.alignment = { horizontal: 'center' };
    });

    // Data rows
    items.forEach(item => {
        const row = sheet.addRow([
            item.processStepNumber || '',
            item.processStepName || '',
            item.processElement || '',
            item.failureMode || '',
            item.failureEffectEnd || '',
            item.failureCause || '',
            item.severity || '',
            item.occurrence || '',
            item.detection || '',
            item.actionPriority || '',
            (item.preventionControls || []).join(', '),
            (item.detectionControls || []).join(', '),
            item.status || '',
        ]);

        // Color code AP column
        const apCell = row.getCell(10);
        if (item.actionPriority === 'H') {
            apCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EF4444' } };
            apCell.font = { color: { argb: 'FFFFFF' }, bold: true };
        } else if (item.actionPriority === 'M') {
            apCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FBBF24' } };
        } else if (item.actionPriority === 'L') {
            apCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '22C55E' } };
            apCell.font = { color: { argb: 'FFFFFF' } };
        }
    });

    // Auto-fit columns
    sheet.columns.forEach(column => {
        column.width = 15;
    });

    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `PFMEA_Worksheet_${projectName.replace(/\s+/g, '_')}.xlsx`);
}

export async function exportControlPlanExcel(
    controlPlanItems: ControlPlanItem[],
    projectName: string
): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Control Plan');

    // Header
    sheet.mergeCells('A1:J1');
    sheet.getCell('A1').value = `Control Plan - ${projectName}`;
    sheet.getCell('A1').font = { size: 16, bold: true };

    // Column headers
    const headers = [
        'Operation', 'Part/Process', 'Characteristic', 'Specification',
        'Evaluation Method', 'Sample Size', 'Sample Freq', 'Control Method',
        'Reaction Plan', 'Responsible'
    ];

    const headerRow = sheet.addRow(headers);
    headerRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '22C55E' } };
    });

    // Data
    controlPlanItems.forEach(item => {
        sheet.addRow([
            item.processStepNumber || '',
            item.processStepName || '',
            item.characteristic || '',
            item.specification || '',
            item.evaluationTechnique || '',
            item.sampleSize || '',
            item.sampleFrequency || '',
            item.controlMethod || '',
            item.reactionPlan || '',
            '', // Responsible - not in current interface
        ]);
    });

    // Auto-fit
    sheet.columns.forEach(col => { col.width = 18; });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Control_Plan_${projectName.replace(/\s+/g, '_')}.xlsx`);
}

// ============================================================
// CSV EXPORT
// ============================================================

export function exportAuditTrailCSV(
    auditEvents: { timestamp: string; user: string; action: string; details: string }[]
): void {
    const headers = ['Timestamp', 'User', 'Action', 'Details'];
    const rows = auditEvents.map(e => [e.timestamp, e.user, e.action, e.details]);

    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `Audit_Trail_${new Date().toISOString().split('T')[0]}.csv`);
}

// ============================================================
// PROCESS FLOW PDF EXPORT
// ============================================================

export async function exportProcessFlowPDF(
    steps: ProcessStep[],
    projectName: string,
    _lineInfo?: { name: string; type: string }[]
): Promise<void> {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });

    // Header
    doc.setFillColor(16, 185, 129);
    doc.rect(0, 0, 420, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Process Flow Diagram', 14, 18);
    doc.setFontSize(10);
    doc.text('Project: ' + projectName, 14, 26);
    doc.setTextColor(0, 0, 0);

    // Table
    const tableData = steps.map(step => [
        step.stepNumber || '',
        step.stepName || '',
        step.stepType || '',
        step.description || '',
        step.equipment || '',
        (step.specialCharacteristics || []).join(', '),
    ]);

    autoTable(doc, {
        startY: 40,
        head: [['Step #', 'Step Name', 'Type', 'Description', 'Equipment', 'Special Char.']],
        body: tableData,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [16, 185, 129] },
    });

    doc.save('Process_Flow_' + projectName.replace(/\s+/g, '_') + '.pdf');
}
